import type { EsConditionConfig } from "@/components/ConditionPageLayoutEs";
import {
  BRAIN_IMG,
  ES_REVIEWED_BY,
  ES_LAST_UPDATED,
  esBaseCrumbs,
} from "./esConditionShared";

export const esAnxiety: EsConditionConfig = {
  canonical: "/es/anxiety-treatment-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento de la ansiedad con ketamina",
  subtitle:
    "Descubra alivio rápido y esperanza renovada con terapia de ketamina IV para los trastornos de ansiedad en Neuro Wellness TMS Centers of America en Coral Springs, FL.",
  introHeading:
    "Descubra alivio y esperanza renovada con ketamina para el tratamiento de la ansiedad en Coral Springs, FL",
  introText: [
    "¿La ansiedad y sus síntomas incapacitantes le impiden vivir su vida al máximo? En Neuro Wellness TMS Centers of America entendemos el impacto profundo que la ansiedad puede tener en su vida diaria, sus relaciones y su bienestar general.",
    "Por eso ofrecemos una solución innovadora y eficaz para el tratamiento de la ansiedad en Coral Springs, FL: la terapia de infusión de ketamina IV.",
  ],
  whatIsTitle: "¿Qué es la ketamina?",
  whatIsText: [
    "La ketamina es un medicamento que se ha utilizado durante décadas como anestésico. Investigaciones científicas recientes han demostrado que la infusión de ketamina en dosis baja también puede ser muy eficaz en el tratamiento de los trastornos de ansiedad.",
    "La ketamina actúa sobre determinados receptores del cerebro, lo que conduce al alivio de los síntomas de la ansiedad y promueve una sensación de calma y bienestar.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina a tratar la ansiedad?",
  howItHelpsText: [
    "La ketamina actúa de manera diferente a los medicamentos ansiolíticos tradicionales, como las benzodiacepinas y los ISRS. Mientras que estos pueden tardar semanas o meses en mostrar resultados, la ketamina tiene el potencial de proporcionar alivio rápido modulando el glutamato, un neurotransmisor importante que ayuda a regular el estado de ánimo y los niveles de ansiedad.",
    "Al dirigirse a los receptores NMDA del cerebro, la ketamina facilita el crecimiento de nuevas conexiones sinápticas y ayuda a restablecer las vías neurales asociadas con la ansiedad.",
  ],
  treatmentExperienceTitle:
    "¿Cómo es la experiencia durante un tratamiento con ketamina?",
  treatmentExperienceText: [
    "Durante una sesión en nuestras instalaciones de vanguardia en Coral Springs, FL, será supervisado de cerca por nuestros profesionales médicos experimentados. El tratamiento consiste en la infusión lenta de una dosis baja de ketamina por vía intravenosa, generalmente durante 40 a 60 minutos.",
    "Muchas personas informan una sensación de relajación profunda y desapego de los pensamientos que provocan ansiedad. Nuestro equipo médico capacitado estará a su lado durante todo el tratamiento.",
  ],
  benefitsTitle: "Los beneficios del tratamiento con ketamina para la ansiedad",
  benefits: [
    {
      title: "Alivio rápido",
      desc: "La ketamina puede proporcionar un alivio rápido de los síntomas de ansiedad, a menudo dentro de horas o días del tratamiento inicial.",
    },
    {
      title: "Resultados duraderos",
      desc: "Muchas personas experimentan una reducción significativa de los síntomas que puede durar semanas o meses después del curso de tratamiento.",
    },
    {
      title: "Opción de tratamiento alternativa",
      desc: "Para quienes han probado otros medicamentos sin éxito, la ketamina ofrece un mecanismo de acción diferente.",
    },
    {
      title: "Efectos secundarios mínimos",
      desc: "La ketamina generalmente es bien tolerada; los efectos secundarios asociados con la infusión de dosis baja suelen ser leves y transitorios.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento de la ansiedad en Coral Springs, FL",
  activeConditionHref: "/es/anxiety-treatment-coral-springs-fl",
  seoTitle:
    "Ketamina para el tratamiento de la ansiedad en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Descubra alivio rápido de la ansiedad con terapia de ketamina IV en Neuro Wellness TMS Centers of America en Coral Springs, FL. Tratamiento seguro y eficaz para la ansiedad grave y el pánico.",
  keywords:
    "ketamina para la ansiedad Coral Springs, tratamiento de ansiedad Coral Springs FL, infusión de ketamina IV ansiedad",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "Ansiedad",
    "/es/anxiety-treatment-coral-springs-fl",
  ),
};

export const esDepression: EsConditionConfig = {
  canonical: "/es/depression-treatment-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento de la depresión con ketamina",
  subtitle:
    "Encuentre alivio rápido de los síntomas depresivos con infusión de ketamina IV en Neuro Wellness TMS Centers of America en Coral Springs, FL.",
  introHeading:
    "Alivio rápido de la depresión con tratamiento de ketamina IV en Coral Springs, FL",
  introText: [
    "La depresión es más que tristeza pasajera; es una afección médica que afecta sus pensamientos, su estado de ánimo, su comportamiento y su salud física. Cuando los antidepresivos tradicionales no ofrecen suficiente alivio, la terapia de infusión de ketamina IV puede ayudar.",
    "En Neuro Wellness TMS Centers of America ofrecemos tratamiento con ketamina IV como una opción basada en evidencia para los adultos con depresión resistente al tratamiento en Coral Springs, FL.",
  ],
  whatIsTitle: "¿Qué es la ketamina para la depresión?",
  whatIsText: [
    "La ketamina, utilizada durante mucho tiempo como anestésico, ha demostrado en investigaciones recientes un efecto rápido sobre los síntomas depresivos, incluso en pacientes que no han respondido a múltiples antidepresivos.",
    "La infusión de ketamina IV en dosis baja actúa sobre el glutamato y otros receptores del cerebro para restablecer la actividad de los circuitos del estado de ánimo, a menudo con un inicio de acción mucho más rápido que los antidepresivos orales.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina a tratar la depresión?",
  howItHelpsText: [
    "A diferencia de los antidepresivos orales, que pueden tardar semanas en hacer efecto, la ketamina puede ofrecer un alivio notable en horas o días al modular el glutamato, un neurotransmisor clave en la regulación del estado de ánimo.",
    "Al estimular el crecimiento de nuevas conexiones sinápticas, la ketamina ayuda a reactivar los circuitos cerebrales inactivos que contribuyen a la depresión, con beneficios que pueden durar semanas o meses.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, bajo la supervisión de nuestro equipo médico. La infusión intravenosa de dosis baja se administra lentamente, generalmente durante 40 a 60 minutos.",
    "Permanece en un entorno cómodo y controlado durante toda la sesión, con nuestro equipo presente para garantizar su seguridad y comodidad.",
  ],
  benefitsTitle: "Beneficios del tratamiento con ketamina para la depresión",
  benefits: [
    {
      title: "Alivio rápido",
      desc: "La ketamina puede ofrecer un alivio de los síntomas depresivos en horas o días, en lugar de las semanas que suelen necesitar los antidepresivos orales.",
    },
    {
      title: "Resultados duraderos",
      desc: "Muchos pacientes experimentan una reducción significativa de los síntomas que puede durar semanas o meses después del curso de tratamiento.",
    },
    {
      title: "Una alternativa cuando los medicamentos no son suficientes",
      desc: "Para quienes no han encontrado alivio con los antidepresivos tradicionales, la ketamina ofrece un mecanismo de acción diferente.",
    },
    {
      title: "Efectos secundarios generalmente leves",
      desc: "La infusión de ketamina en dosis baja suele ser bien tolerada, con efectos secundarios transitorios.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento de la depresión en Coral Springs, FL",
  activeConditionHref: "/es/depression-treatment-coral-springs-fl",
  seoTitle:
    "Ketamina para el tratamiento de la depresión en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Alivio rápido de la depresión con infusión de ketamina IV en Neuro Wellness TMS Centers of America en Coral Springs, FL. Tratamiento basado en evidencia para la depresión resistente al tratamiento.",
  keywords:
    "ketamina para la depresión Coral Springs, tratamiento de depresión Coral Springs FL, depresión resistente al tratamiento ketamina",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "Depresión",
    "/es/depression-treatment-coral-springs-fl",
  ),
};

export const esBipolar: EsConditionConfig = {
  canonical: "/es/bipolar-disorder-coral-springs-fl",
  badge: "Terapia con ketamina IV",
  title: "Tratamiento del trastorno bipolar con ketamina",
  subtitle:
    "Cuando los estabilizadores del estado de ánimo no son suficientes, la ketamina IV puede ayudar con los episodios depresivos del trastorno bipolar en Coral Springs, FL.",
  introHeading:
    "Tratamiento del trastorno bipolar con ketamina IV en Coral Springs, FL",
  introText: [
    "El trastorno bipolar es una afección compleja marcada por cambios extremos en el estado de ánimo. Los episodios depresivos pueden ser profundos y difíciles de tratar, incluso con varios medicamentos estabilizadores del estado de ánimo.",
    "Ofrecemos infusión de ketamina IV como una opción complementaria para los episodios depresivos del trastorno bipolar, siempre bajo la supervisión de su psiquiatra y como parte de un plan de tratamiento integral.",
  ],
  whatIsTitle: "¿Qué es la ketamina para el trastorno bipolar?",
  whatIsText: [
    "La ketamina IV ha mostrado en estudios un efecto rápido sobre los síntomas depresivos en algunos pacientes con trastorno bipolar, particularmente durante los episodios depresivos resistentes al tratamiento.",
    "Actúa sobre el glutamato y otros receptores para restablecer la actividad de los circuitos del estado de ánimo, con un inicio de acción potencialmente más rápido que muchos antidepresivos orales.",
  ],
  howItHelpsTitle: "¿Cómo ayuda la ketamina con el trastorno bipolar?",
  howItHelpsText: [
    "La ketamina se dirige a los circuitos cerebrales implicados en la regulación del estado de ánimo. Para los pacientes con episodios depresivos profundos, puede ofrecer un alivio más rápido que los enfoques tradicionales.",
    "Se utiliza como parte de un plan integral y coordinado con su psiquiatra, junto con los estabilizadores del estado de ánimo que ya toma, no como sustitución de ellos.",
  ],
  treatmentExperienceTitle: "¿Cómo es una sesión de tratamiento con ketamina?",
  treatmentExperienceText: [
    "Cada sesión se realiza en nuestro centro en Coral Springs, FL, con supervisión médica continua. La infusión de dosis baja se administra lentamente durante aproximadamente 40 a 60 minutos en un entorno controlado.",
    "Nuestro equipo clínico está presente durante toda la sesión para garantizar su seguridad, comodidad y una respuesta inmediata a cualquier necesidad.",
  ],
  benefitsTitle:
    "Beneficios del tratamiento con ketamina para el trastorno bipolar",
  benefits: [
    {
      title: "Alivio rápido del episodio depresivo",
      desc: "La ketamina puede ofrecer un alivio en horas o días, en lugar de las semanas que suelen necesitar los ajustes de medicación.",
    },
    {
      title: "Enfoque complementario",
      desc: "Se utiliza junto con su plan de tratamiento existente, bajo la coordinación de su psiquiatra.",
    },
    {
      title: "Basado en evidencia",
      desc: "La infusión de ketamina IV cuenta con un cuerpo creciente de investigación para los episodios depresivos resistentes al tratamiento.",
    },
    {
      title: "Cuidado ambulatorio",
      desc: "El tratamiento se realiza en consultorio, sin necesidad de hospitalización.",
    },
  ],
  imageUrl: BRAIN_IMG,
  imageAlt: "Tratamiento del trastorno bipolar en Coral Springs, FL",
  activeConditionHref: "/es/bipolar-disorder-coral-springs-fl",
  seoTitle:
    "Ketamina para el trastorno bipolar en Coral Springs, FL | Neuro Wellness",
  seoDescription:
    "Infusión de ketamina IV para los episodios depresivos del trastorno bipolar en Coral Springs, FL. Cuidado complementario coordinado con su psiquiatra.",
  keywords:
    "ketamina trastorno bipolar Coral Springs, tratamiento bipolar Coral Springs FL, episodio depresivo bipolar ketamina",
  reviewedBy: ES_REVIEWED_BY,
  lastUpdated: ES_LAST_UPDATED,
  breadcrumbs: esBaseCrumbs(
    "Trastorno bipolar",
    "/es/bipolar-disorder-coral-springs-fl",
  ),
};
